import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { Formik } from 'formik';
import type { FormikProps } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, Grid, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import TooltipField from 'src/components/atoms/TooltipField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import useSubmit from 'src/hooks/forms/useSubmit';
import { useSupplierProductOptions } from 'src/hooks/forms/useOptions';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';
import { buildSupplierProductRequest } from 'src/utils/submit/submitRequests';
import randomId from 'src/utils/randomId';
import { useProductInSupplierForm } from 'src/hooks/forms/useProductInSupplierForm';
import { useSupplierProductForm } from 'src/hooks/forms/useForms';
import { useSuppliersById } from 'src/hooks/api/requestConfigs/purchasing';
import { useSelectProductOnSupplierProduct } from 'src/hooks/table/configs/useSelectProductOnSupplierProduct';
import SelectTableDialog from 'src/components/organisms/tables/SelectTableDialog';
import FormHelper from 'src/utils/formHelper';
import { FormInfoButton } from 'src/components/atoms/buttons/FormInfoButton';
import { useVideoDialog, VideoButton, VideoDialog } from 'src/components/molecules/video-dialog/VideoDialog';

function useSetCurrencyFromSupplier(supplierId: any, setFieldValue: any, currencyOptions: any, isEditPage: boolean) {
    const idToPass = isEditPage ? null : supplierId;
    const supplier: any = useSuppliersById(idToPass);

    useEffect(() => {
        if (!isEditPage) {
            if (supplier && supplier?.CurrencyId) {
                const matchedCurrency = currencyOptions?.find((option: any) => option.value === supplier.CurrencyId);
                if (matchedCurrency) {
                    setFieldValue("Currency_Value", matchedCurrency.label);
                }
            }
        }
    }, [supplier, currencyOptions, isEditPage, setFieldValue]);
}

const requiredSupplierProductFields = [
    'SupplierProduct_Value',
    'SupplierProduct',
    'SupplierAssortment',
    'ValidFrom',
    'Price',
    'Status',
    'Manufacturer',
    'ManufacturerProduct',
];

const scheme = Yup.object().shape({
    SupplierProduct_Value: Yup.string()
        .required('Enter the supplier product name (the name your supplier uses for this item).'),

    SupplierProduct: Yup.string()
        .required('Enter the supplier product number (the ID your supplier uses for this product).'),

    SupplierAssortment: Yup.string()
        .required('Select which supplier this product belongs to.'),

    ValidFrom: Yup.date()
        .typeError('Enter a valid start date.')
        .required('Select the date from which this supplier product is valid.'),

    Price: Yup.number()
        .typeError('Enter a valid numeric price.')
        .required('Enter the supplier price for this product.'),

    Status: Yup.string()
        .required('Select the status for this supplier product (Active or Inactive).'),

    Manufacturer: Yup.mixed()
        .required(
            'Select a Manufacturer, or use Product Data by importing one with the + button above'
        ),

    ManufacturerProduct: Yup.string()
        .required('Enter the manufacturer product ID in the ManufacturerProduct field. If this supplier product is not linked to an existing Product, this value must be added directly on the supplier product.'),
});

const defaultInitialValues: any = {
    SupplierProduct: "",
    SupplierProduct_Value: "",
    SupplierAssortment: "",
    SupplierName: "",
    ValidFrom: new Date().toISOString().split('T')[0],
    ValidTo: null,
    Price: "",
    ETA: null,
    CurrencyConvertedPrice: "",
    Currency_Value: "",
    QuantityAvailableAtSupplier: "",
    Status: 2,

    Product: "",
    Manufacturer: "",
    ManufacturerProduct: "",
    ManufacturerProduct_Value: "",
    ProductType: "",
    EAN: "",
    TARIC: "",
    ProductIsLocked: false,

    UpdatedAt: "",
    Modified: "",
    Created: "",
    isEditPage: "",
};

const testProductValues = [
    { SupplierProduct: "Test Supplier Prouct Webclient " + randomId() },
    { Manufacturer: 4 },
    { SupplierProduct_Value: "NR " + randomId() },
    { SupplierAssortment: 1 },
    { ProductType: 3 },
    { Product: "1000020" },
    { ManufacturerProduct: "11" + randomId() },
    { ManufacturerProduct_Value: "Testprodukt ManName Webclient " + randomId() },
    { TARIC: 1234567 },
    { Price: 1001 },
    { CurrencyConvertedPrice: 100.1 },
    { ValidFrom: "2024-01-16T00:00:00Z" },
    { ValidTo: null },
    { QuantityAvailableAtSupplier: 1 },
    { Status: 1 },
]

function EditSupplierProductWithAiButtons() {
    const [editId, PageName, currentSubmitFunction, productId, isEditPage] = FormHelper.getEditPageConfig("Supplier Product", buildSupplierProductRequest, buildSupplierProductRequest);
    const [result, submit] = useSubmit(currentSubmitFunction);
    const newInitialValues = { ...defaultInitialValues, ...useProductInSupplierForm(productId) }
    const initialValues = useSupplierProductForm(editId, Object.keys(newInitialValues).length === 0 ? defaultInitialValues : newInitialValues, result)
    const [supplierOptions, manufacturerOptions, currencyOptions] = useSupplierProductOptions();
    const videoRef = useVideoDialog("https://www.youtube.com/embed/gMQnagSLlg4?si=5mzyCQ_-nHVOanRe");

    useRedirectOnCreate(result, editId, "SupplierAssortmentItemId", "/products/supplier-products/edit-test?id=");
    //const { selectedProduct, setSelectedProduct, showProductSelector, setShowProductSelector } = useSelectProductInForm();
    return (
        <>
            <Formik
                initialValues={initialValues}
                validateOnBlur={true}
                validateOnChange={false}
                validationSchema={scheme}
                onSubmit={submit}
                enableReinitialize
            >
                {(props): JSX.Element => {
                    //useUpdateFormikWithSelectedProduct(props.values, props.setFieldValue, selectedProduct);
                    const productSelector: any = useSelectProductOnSupplierProduct(props);
                    const productSelectorDialogProps = getSupplierProductSelectorDialogProps(productSelector);
                    const productOptions = getSupplierProductRowsFromSelector(productSelector);
                    useSetCurrencyFromSupplier(props.values.SupplierAssortment, props.setFieldValue, currencyOptions, isEditPage);

                    return (
                        <SupplierProductLlmClipboardProvider
                            formikProps={props}
                            pageContext={{
                                entityName: "Supplier Product",
                                pageName: getPageNameText(PageName),
                                editId,
                                initialValues,
                            }}
                            supplierProductOptionsContext={{
                                supplierOptions,
                                manufacturerOptions,
                                currencyOptions,
                                productOptions,
                            }}
                        >
                            <div>
                                <PageHeader  {...{
                                    title: PageName,
                                    initialValues: initialValues,
                                    subTitle: "Create and edit Supplier Products",
                                    components: [
                                        { component: (<FormInfoButton formikProps={props} />), },
                                        { component: <VideoButton videoRef={videoRef} /> },
                                        { component: <SupplierProductLlmCopyButton /> },
                                        { component: <SupplierProductLlmIncludeProductDataToggle /> },
                                        { component: <SupplierProductLlmPasteButton /> },
                                        { component: <SupplierProductLlmAiConfigButton /> },
                                        { component: <SupplierProductLlmAiRunButton /> },
                                        { component: <CustomButton isSubmitting={props.isSubmitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> },
                                        {
                                            isInGroupedMenu: true, component: <CustomButton
                                                disabled={props.values.ProductIsLocked}
                                                variant="text" title="New Supplier Product"
                                                icon={<AddTwoToneIcon />} actionUrl="/products/supplier-products/create" />
                                        },
                                        { isInGroupedMenu: true, component: <CustomButton isSubmitting={props.isSubmitting} variant="text" onClick={() => testProductValues.forEach(field => { props.setFieldValue(Object.keys(field)[0], field[Object.keys(field)[0]]) })} title="Insert Test Data" /> },
                                        { isInGroupedMenu: true, component: <CustomButton isSubmitting={props.isSubmitting} variant="text" onClick={() => productSelector.openSelector()} title="Create from product" /> },
                                    ],
                                    alertData: { dataArray: (result ?? false) ? [result] : null }
                                }} />
                                <SelectTableDialog {...productSelectorDialogProps} />
                                <ContentCardsContainer >
                                    <ContentCard {...{ cardId: "Supplier Product info", md: 6 }}>
                                        <SupplierProductLlmFormContainer formikProps={props}>
                                            <SupplierProductLlmFormField fieldProps={{ name: "SupplierProduct_Value", label: "Supplier Product Name", md: 12 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "SupplierProduct", label: "Supplier Product Number", md: 5.8, tooltipText: "The Id this supplier uses for this product, often the same as manufacturer product Id" }} formikProps={props}></SupplierProductLlmFormField>

                                            {isEditPage ?
                                                <SupplierProductLlmFormField fieldProps={{ name: "SupplierName", disabled:props.values.ProductIsLocked ?? false, label: "Supplier", md: 12 }} formikProps={props} />
                                                :
                                                <SupplierProductLlmFormField fieldProps={{ name: "SupplierAssortment", select: true, label: "Supplier", md: 12 }} formikProps={props}>
                                                    {supplierOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                </SupplierProductLlmFormField>
                                            }
                                            <SupplierProductLlmFormField fieldProps={{ name: "ValidFrom", date: true, label: "Valid From", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "ValidTo", date: true, label: "Valid To", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Price", label: "Price", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "ETA", date: true, label: "ETA", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "CurrencyConvertedPrice", disabled:true, label: "Currency Converted Price", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Currency_Value", disabled: true, label: "Currency", md: 3.8 }} formikProps={props}>
                                            </SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "QuantityAvailableAtSupplier", label: "Quantity Available At Supplier", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Status", select: true, label: "Status", md: 5.8 }} formikProps={props}>
                                                <MenuItem value="1">Active</MenuItem>
                                                <MenuItem value="2">Inactive</MenuItem>
                                            </SupplierProductLlmFormField>
                                        </SupplierProductLlmFormContainer>
                                    </ContentCard>

                                    <ContentCard {...{ cardId: "Related Product info", md: 6 }}>
                                        <SupplierProductLlmFormContainer formikProps={props}>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Product", disabled:props.values.ProductIsLocked ?? false, label: "Related Product Id", md: 10 }} formikProps={props} />
                                            <CustomButton variant="contained" disabled={props.values.ProductIsLocked} iconButton={true} icon={<AddTwoToneIcon />} tooltipText='Select Product' onClick={() => productSelector.openSelector()} />

                                            <SupplierProductLlmFormField fieldProps={{ name: "Manufacturer",  disabled:props.values.ProductIsLocked ?? false, select: true, label: "Manufacturer Name", md: 12 }} formikProps={props}>
                                                {manufacturerOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "ManufacturerProduct", disabled:props.values.ProductIsLocked ?? false, label: "Manufacturer Product Id", md: 5.8, tooltipText: "Required. The manufacturer product ID/code. If no related Product is selected, this must be filled directly on the supplier product." }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "ManufacturerProduct_Value", disabled:props.values.ProductIsLocked ?? false,label: "Manufacturer Product Name", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "ProductType",disabled:true, label: "Product Type", md: 12 }} formikProps={props}>
                                            </SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "EAN",  label: "EAN", md: 5.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "TARIC", label: "TARIC", md: 5.8, tooltipText: 'See here for correct TARIC codes: tariffnumber.com' }} formikProps={props}></SupplierProductLlmFormField>
                                        </SupplierProductLlmFormContainer>
                                    </ContentCard>

                                    <ContentCard {...{ cardId: "System Info", md: 12, cardTypes: [CardType.collapsable] }}>
                                        <SupplierProductLlmFormContainer formikProps={props}>
                                            <SupplierProductLlmFormField fieldProps={{ name: "UpdatedAt", date: true, disabled: true, label: "Updated At", md: 3.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Modified", date: true, disabled: true, label: "Modified", md: 3.8 }} formikProps={props}></SupplierProductLlmFormField>
                                            <SupplierProductLlmFormField fieldProps={{ name: "Created", date: true, disabled: true, label: "Created", md: 3.8 }} formikProps={props}></SupplierProductLlmFormField>
                                        </SupplierProductLlmFormContainer>
                                    </ContentCard>
                                </ContentCardsContainer>

                                <VideoDialog videoRef={videoRef} />

                                <Footer />
                            </div>
                        </SupplierProductLlmClipboardProvider>
                    );
                }}
            </Formik>
        </>
    );
}

type SupplierProductLlmOptionsContext = {
    supplierOptions: any;
    manufacturerOptions: any;
    currencyOptions: any;
    productOptions?: any;
};

type SupplierProductLlmPageContext = {
    entityName: string;
    pageName: string;
    editId: any;
    initialValues: any;
};

type SupplierProductLlmChangedField = {
    fieldName: string;
    originalValue: any;
    pastedValue: any;
};

type SupplierProductLlmChangedFields = Record<string, SupplierProductLlmChangedField>;

type SupplierProductLlmClipboardController = {
    copyPromptToClipboard: (event?: React.MouseEvent) => Promise<void>;
    pasteValuesFromClipboard: (event?: React.MouseEvent) => Promise<void>;
    applyLlmResponseText: (responseText: string) => Promise<SupplierProductLlmApplyResult>;
    buildPrompt: () => string;
    revertField: (fieldName: string) => Promise<void>;
    copyButtonTitle: string;
    pasteButtonTitle: string;
    changedFields: SupplierProductLlmChangedFields;
    includeProductData: boolean;
    setIncludeProductData: React.Dispatch<React.SetStateAction<boolean>>;
};

type SupplierProductLlmApplyResult = 'applied' | 'no-fields' | 'no-changes';

type UseSupplierProductLlmClipboardArgs = {
    formikProps: FormikProps<any>;
    pageContext: SupplierProductLlmPageContext;
    supplierProductOptionsContext: SupplierProductLlmOptionsContext;
    additionalContext?: Record<string, unknown>;
};

type SupplierProductLlmClipboardProviderProps = UseSupplierProductLlmClipboardArgs & {
    children: React.ReactNode;
};

const SupplierProductLlmClipboardContext = React.createContext<SupplierProductLlmClipboardController | null>(null);

const supplierProductLlmReadOnlyFields = [
    "Id",
    "SupplierAssortmentItemId",
    "SupplierName",
    "Currency_Value",
    "CurrencyConvertedPrice",
    "ProductType",
    "ProductIsLocked",
    "UpdatedAt",
    "Created",
    "Modified",
    "isEditPage",
];

const supplierProductLlmEditableFields = [
    "SupplierProduct_Value",
    "SupplierProduct",
    "SupplierAssortment",
    "ValidFrom",
    "ValidTo",
    "Price",
    "ETA",
    "QuantityAvailableAtSupplier",
    "Status",
    "Product",
    "Manufacturer",
    "ManufacturerProduct",
    "ManufacturerProduct_Value",
    "EAN",
    "TARIC",
];

const supplierProductLlmProductLockedFields = [
    "Product",
    "Manufacturer",
    "ManufacturerProduct",
    "ManufacturerProduct_Value",
    "ProductType",
];

const commonSupplierProductLlmJsonReturnPrompt = `
Return only valid JSON in a codeblock for easy copy paste. After the codeblock, if relevant you can provide explanations as well as links to support relevant data.

Expected shape:
{
  "values": {
    "SupplierProduct_Value": "Updated supplier product name",
    "Manufacturer": "value-from-available-options",
    "ManufacturerProduct": "manufacturer-product-id-or-code"
  }
}

Rules:
- Only include fields that should be changed.
- When creating a new supplier product, include all required fields listed in requiredFields when you have enough data.
- ManufacturerProduct is required by validation. If currentValues.ManufacturerProduct is empty and you are creating or completing a supplier product, you must include the manufacturer product ID/code using the exact key "ManufacturerProduct".
- Do not put the manufacturer product ID/code in "ManufacturerProduct_Value". ManufacturerProduct_Value is the manufacturer product name/description field.
- The keys inside "values" must match the Formik field names exactly.
- Keep booleans as booleans, numbers as numbers, and strings as strings.
- Do not include read-only/system fields unless explicitly asked.
`.trim();

const commonSupplierProductLlmOptionsPrompt = `
When a field has available options, use the option value in the returned JSON, not the display label.
Compact option objects use this shape: { "Display label": value }.
The productOptions list, when included, uses this compact tuple shape: { "schema": ["ProductId", "Name", "Manufacturer", "ManufacturerProductId"], "rows": [["113222", "Stol kopia", 794, "epokjonas999"]] }.
If using a row from productOptions:
- Put ProductId into values.Product.
- Put Manufacturer into values.Manufacturer.
- Put ManufacturerProductId into values.ManufacturerProduct.
If the correct option is unclear, keep the existing value instead of guessing.
`.trim();

const supplierProductLlmPrompt = `
You are helping update a Supplier Product form.
Use the current form values, original initial values, available select options, required fields, compact product options, and page context below.
Return a JSON object that can be pasted back into the form to update the supplier product.

General supplier product rules:
- A supplier product can either be linked to an existing Product or created without a related Product.
- If it is linked to an existing Product, Product should contain that product ID and related manufacturer data can come from the matched product data.
- If it is not linked to an existing Product, the supplier product must still contain its own manufacturer product ID/code.
- The manufacturer product ID/code must always be returned in the Formik field named "ManufacturerProduct".
- ManufacturerProduct is required. ManufacturerProduct_Value is not the same field; it is the manufacturer product name/description.
- Do not create or select a related Product unless the user explicitly asks for product matching or product linking.
`.trim();

function getSupplierProductLlmProductDataModePrompt(includeProductData: boolean) {
    if (includeProductData) {
        return 'Product data is included in this prompt. If you link to an existing product, use productOptions to choose Product, Manufacturer, and ManufacturerProduct. ProductOptions rows use this schema: ["ProductId", "Name", "Manufacturer", "ManufacturerProductId"]. Put ProductId into values.Product, Manufacturer into values.Manufacturer, and ManufacturerProductId into values.ManufacturerProduct. ManufacturerProduct is required; if currentValues.ManufacturerProduct is empty, include a manufacturer product ID/code in values.ManufacturerProduct.';
    }

    return 'Product data is not included in this prompt. Do not set Product unless the user explicitly provided a product ID. Since there is no product data to copy from, any supplier product you create or complete must include a manufacturer product ID/code in values.ManufacturerProduct.';
}

function SupplierProductLlmClipboardProvider({ children, ...hookArgs }: SupplierProductLlmClipboardProviderProps) {
    const supplierProductLlmClipboard = useSupplierProductLlmClipboard(hookArgs);

    return (
        <SupplierProductLlmClipboardContext.Provider value={supplierProductLlmClipboard}>
            {children}
        </SupplierProductLlmClipboardContext.Provider>
    );
}

function useSupplierProductLlmClipboardContext() {
    const supplierProductLlmClipboard = useContext(SupplierProductLlmClipboardContext);

    if (!supplierProductLlmClipboard) {
        throw new Error("SupplierProductLlmClipboardContext is missing.");
    }

    return supplierProductLlmClipboard;
}

function SupplierProductLlmCopyButton() {
    const supplierProductLlmClipboard = useSupplierProductLlmClipboardContext();

    return <CustomButton variant="text" title={supplierProductLlmClipboard.copyButtonTitle} icon={<ContentCopyIcon />} onClick={supplierProductLlmClipboard.copyPromptToClipboard} />;
}

function SupplierProductLlmPasteButton() {
    const supplierProductLlmClipboard = useSupplierProductLlmClipboardContext();

    return <CustomButton variant="text" title={supplierProductLlmClipboard.pasteButtonTitle} icon={<ContentPasteIcon />} onClick={supplierProductLlmClipboard.pasteValuesFromClipboard} />;
}

function SupplierProductLlmIncludeProductDataToggle() {
    const supplierProductLlmClipboard = useSupplierProductLlmClipboardContext();

    return (
        <FormControlLabel
            sx={{ ml: 0.5, mr: 0.5 }}
            control={
                <Checkbox
                    size="small"
                    checked={supplierProductLlmClipboard.includeProductData}
                    onChange={(event) => supplierProductLlmClipboard.setIncludeProductData(event.target.checked)}
                />
            }
            label="Product data"
        />
    );
}

function SupplierProductLlmAiConfigButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Config" icon={<SettingsIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <SupplierProductLlmAiConfigDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}

function SupplierProductLlmAiRunButton() {
    const supplierProductLlmClipboard = useSupplierProductLlmClipboardContext();
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Run" icon={<AutoAwesomeIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <SupplierProductLlmAiRunDialog
                open={open}
                onClose={() => setOpen(false)}
                buildPrompt={supplierProductLlmClipboard.buildPrompt}
                applyLlmResponseText={supplierProductLlmClipboard.applyLlmResponseText}
            />
        </>
    );
}

function useSupplierProductLlmClipboard({ formikProps, pageContext, supplierProductOptionsContext, additionalContext = {} }: UseSupplierProductLlmClipboardArgs): SupplierProductLlmClipboardController {
    const [copyButtonTitle, setCopyButtonTitle] = useState<string>("Copy");
    const [pasteButtonTitle, setPasteButtonTitle] = useState<string>("Paste");
    const [changedFields, setChangedFields] = useState<SupplierProductLlmChangedFields>({});
    const [includeProductData, setIncludeProductData] = useState<boolean>(false);

    const setTemporaryButtonTitle = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, temporaryTitle: string, defaultTitle: string) => {
        setter(temporaryTitle);
        window.setTimeout(() => setter(defaultTitle), 1800);
    }, []);

    const getPromptData = useCallback(() => {
        const fieldOptions = {
            SupplierAssortment: compactSupplierProductLabelValueOptions(supplierProductOptionsContext.supplierOptions),
            Manufacturer: compactSupplierProductLabelValueOptions(supplierProductOptionsContext.manufacturerOptions),
            Status: {
                Active: "1",
                Inactive: "2",
            },
        };

        const promptData: any = {
            pageContext: {
                entityName: pageContext.entityName,
                pageName: pageContext.pageName,
                editId: pageContext.editId,
            },
            currentValues: formikProps.values,
            initialValues: pageContext.initialValues,
            requiredFields: requiredSupplierProductFields,
            fieldRequirements: {
                SupplierProduct_Value: 'Required. Supplier product name. The name the supplier uses for this item.',
                SupplierProduct: 'Required. Supplier product number. The ID/number the supplier uses for this item.',
                SupplierAssortment: 'Required. Supplier selection. Use the value from fieldOptions.SupplierAssortment.',
                ValidFrom: 'Required. Date from which this supplier product is valid.',
                Price: 'Required. Supplier price.',
                Status: 'Required. Use 1 for Active or 2 for Inactive.',
                Manufacturer: 'Required. Manufacturer selection. Use the value from fieldOptions.Manufacturer.',
                ManufacturerProduct: 'Required. Manufacturer product ID/code. This must be returned with the exact key ManufacturerProduct. Do not use ManufacturerProduct_Value for this ID/code.',
            },
            supplierProductCreationRules: {
                canBeLinkedToExistingProduct: true,
                canBeCreatedWithoutRelatedProduct: true,
                productField: 'Product',
                manufacturerField: 'Manufacturer',
                manufacturerProductIdField: 'ManufacturerProduct',
                manufacturerProductNameField: 'ManufacturerProduct_Value',
                manufacturerProductIdRule: 'ManufacturerProduct is required for new supplier products. If productOptions are included and a product row is used, copy that row ManufacturerProductId into ManufacturerProduct. If Product is empty or no product data is available, add the manufacturer product ID/code directly to ManufacturerProduct.',
            },
            editableFields: supplierProductLlmEditableFields,
            readOnlyFields: supplierProductLlmReadOnlyFields,
            productLockedFields: formikProps.values.ProductIsLocked ? supplierProductLlmProductLockedFields : [],
            changedFields,
            fieldOptions,
            compactDataShapes: {
                fieldOptions: '{ "Display label": value }',
                productOptions: '{ "schema": ["ProductId", "Name", "Manufacturer", "ManufacturerProductId"], "rows": [["113222", "Stol kopia", 794, "epokjonas999"]] }',
            },
            additionalContext,
        };

        if (includeProductData) {
            promptData.productOptions = compactSupplierProductProductOptions(supplierProductOptionsContext.productOptions);
        }

        return promptData;
    }, [additionalContext, changedFields, formikProps.values, includeProductData, pageContext, supplierProductOptionsContext]);

    const buildPrompt = useCallback(() => {
        return [
            supplierProductLlmPrompt,
            getSupplierProductLlmProductDataModePrompt(includeProductData),
            commonSupplierProductLlmJsonReturnPrompt,
            commonSupplierProductLlmOptionsPrompt,
            "Context data:",
            JSON.stringify(getPromptData(), null, 2),
        ].join("\n\n");
    }, [getPromptData, includeProductData]);

    const copyPromptToClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            await writeTextToClipboard(buildPrompt());
            setTemporaryButtonTitle(setCopyButtonTitle, "Copied", "Copy");
        } catch (error) {
            console.error("Failed to copy supplier product LLM prompt.", error);
            setTemporaryButtonTitle(setCopyButtonTitle, "Copy failed", "Copy");
            window.alert("Could not copy the supplier product prompt to the clipboard.");
        }
    }, [buildPrompt, setTemporaryButtonTitle]);

    const applyLlmResponseText = useCallback(async (responseText: string): Promise<SupplierProductLlmApplyResult> => {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const nextValues = getFormikValuesFromLlmResponse(parsedResponse);
        const allowedValues = getAllowedSupplierProductFormikValues(nextValues, formikProps.values);

        if (Object.keys(allowedValues).length === 0) {
            return 'no-fields';
        }

        const currentValues = formikProps.values;
        const changedAllowedValues = getChangedSupplierProductFormikValues(allowedValues, currentValues);

        if (Object.keys(changedAllowedValues).length === 0) {
            return 'no-changes';
        }

        const nextChangedFields = getNextSupplierProductLlmChangedFields(changedFields, changedAllowedValues, currentValues);

        await formikProps.setValues({ ...currentValues, ...changedAllowedValues }, true);
        setChangedFields(nextChangedFields);

        return 'applied';
    }, [changedFields, formikProps]);

    const pasteValuesFromClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
                throw new Error("Clipboard read is not available in this browser.");
            }

            const clipboardText = await navigator.clipboard.readText();
            const applyResult = await applyLlmResponseText(clipboardText);

            if (applyResult === 'no-fields') {
                window.alert('No matching editable Formik fields were found in the pasted JSON. Expected either { "values": { ... } } or a JSON object with Supplier Product Formik field names.');
                return;
            }

            if (applyResult === 'no-changes') {
                setTemporaryButtonTitle(setPasteButtonTitle, "No changes", "Paste");
                return;
            }

            setTemporaryButtonTitle(setPasteButtonTitle, "Pasted", "Paste");
        } catch (error) {
            console.error("Failed to paste supplier product LLM values.", error);
            setTemporaryButtonTitle(setPasteButtonTitle, "Paste failed", "Paste");
            window.alert("Could not paste supplier product values. Make sure the clipboard contains valid JSON from the LLM.");
        }
    }, [applyLlmResponseText, setTemporaryButtonTitle]);

    const revertField = useCallback(async (fieldName: string) => {
        const change = changedFields[fieldName];

        if (!change) {
            return;
        }

        await formikProps.setFieldValue(fieldName, change.originalValue, true);

        setChangedFields((previousChangedFields) => {
            const nextChangedFields = { ...previousChangedFields };
            delete nextChangedFields[fieldName];
            return nextChangedFields;
        });
    }, [changedFields, formikProps]);

    return {
        copyPromptToClipboard,
        pasteValuesFromClipboard,
        applyLlmResponseText,
        buildPrompt,
        revertField,
        copyButtonTitle,
        pasteButtonTitle,
        changedFields,
        includeProductData,
        setIncludeProductData,
    };
}

/**
 * Prototype local copy of the form container behavior.
 * Kept local so this feature can be tested without touching the shared FormContainerMolecule.
 */
function SupplierProductLlmFormContainer({ formikProps, children }: { formikProps: FormikProps<any>; children: React.ReactNode }) {
    return (
        <Grid container spacing={2}>
            {children}
        </Grid>
    );
}

/**
 * Prototype local copy of FormField.
 * Kept local so the diff UI can be tested without changing the shared FormField.
 */
type SupplierProductLlmResolvedType = 'date' | 'checkbox' | 'select' | 'multiline' | 'text';

interface SupplierProductLlmFormFieldProps {
    fieldProps: any;
    formikProps?: any;
    children?: React.ReactNode;
    sx?: SxProps<Theme>;
}

function SupplierProductLlmFormField({ fieldProps, formikProps, children, sx }: SupplierProductLlmFormFieldProps) {
    const label = fieldProps.label ?? (fieldProps.name?.charAt(0).toUpperCase() + fieldProps.name?.slice(1));
    const onChangeFunction = fieldProps?.handleChange ?? formikProps?.handleChange;
    const supplierProductLlmClipboard = useSupplierProductLlmClipboardContext();

    const resolvedType: SupplierProductLlmResolvedType =
        fieldProps.type ??
        (fieldProps.date
            ? 'date'
            : fieldProps.checkbox
                ? 'checkbox'
                : fieldProps.select
                    ? 'select'
                    : fieldProps.multiLine || fieldProps.multiline
                        ? 'multiline'
                        : 'text');

    const name = fieldProps.name;
    const touched = formikProps?.touched?.[name];
    const error = formikProps?.errors?.[name];
    const hasSubmitted = (formikProps?.submitCount ?? 0) > 0;
    const showError = (touched || hasSubmitted) && error;

    // full status object
    const status = formikProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText: string | undefined = fieldProps.tooltipText;

    // Hints from formik.status.hints
    const hints = status?.hints ?? {};
    const hintForField = hints?.[name];
    const showHint = !showError && Boolean(hintForField);

    // Field meta (warning, etc) from schema
    const fieldMeta = status?.fieldMeta?.[name] ?? {};
    const metaWarning: string | undefined = fieldMeta.warning;

    const value = formikProps?.values?.[name];
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    const showWarning =
        !showError &&
        !showHint &&
        Boolean(metaWarning) &&
        (touched || hasSubmitted) &&
        isEmpty;

    const baseHelperText = showError
        ? error
        : showHint
            ? hintForField
            : showWarning
                ? metaWarning
                : '';

    // If info-button mode is on and there is tooltipText, use it as info helper text
    const useTooltipInfo = !baseHelperText && !showError && showAllTooltips && !!tooltipText;

    const helperText = useTooltipInfo ? tooltipText : baseHelperText;

    const isError = Boolean(showError);
    const isInfo = !isError && (showHint || useTooltipInfo);
    const isWarning = !isError && !isInfo && showWarning;

    const diffOptions = getSupplierProductLlmDiffOptions(fieldProps, formikProps, supplierProductLlmClipboard);

    return (
        <>
            <Grid
                item
                display="flex"
                md={fieldProps.md ?? 12}
                xs={fieldProps.xs ?? 5.8}
            >
                <Grid
                    item
                    md={12}
                    xs={12}
                    display="flex"
                    alignItems="center"
                    width="100%"
                >
                    {resolvedType === 'date' ? (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label={label}
                                ampm={false}
                                disabled={fieldProps.disabled ?? false}
                                value={
                                    formikProps?.values?.[fieldProps.name] &&
                                        formikProps?.values?.[fieldProps.name] !== ''
                                        ? parseISO(formikProps?.values?.[fieldProps.name])
                                        : null
                                }
                                onChange={(value) => {
                                    formikProps.setFieldValue(
                                        fieldProps.name,
                                        value ? new Date(value).toISOString() : null
                                    );
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: 'normal',
                                        name: fieldProps.name,
                                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                            formikProps.setFieldTouched(fieldProps.name, true);
                                            formikProps.handleBlur(e);
                                        },
                                        error: Boolean(isError),
                                        helperText,
                                        sx,
                                        FormHelperTextProps: isInfo
                                            ? { sx: { color: 'info.main' } }
                                            : isWarning
                                                ? { sx: { color: 'warning.main' } }
                                                : undefined,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    ) : resolvedType === 'checkbox' ? (
                        <Box display="flex" alignItems="center">
                            <FormControlLabel
                                sx={sx}
                                disabled={fieldProps.disabled ?? false}
                                control={
                                    <Checkbox
                                        name={fieldProps.name}
                                        checked={toBoolean(formikProps?.values?.[fieldProps.name]) ?? false}
                                        value={formikProps?.values?.[fieldProps.name] ?? false}
                                        onBlur={formikProps?.handleBlur}
                                        onChange={onChangeFunction}
                                    />
                                }
                                label={label}
                            />
                            <Stack display="flex" spacing={2}>
                                {Boolean(isError) && (
                                    <FormHelperText error>{helperText}</FormHelperText>
                                )}
                                {!isError && isInfo && (
                                    <FormHelperText sx={{ color: 'info.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                                {!isError && !isInfo && isWarning && (
                                    <FormHelperText sx={{ color: 'warning.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Box>
                    ) : (
                        <SupplierProductLlmCustomField
                            options={{
                                ...fieldProps,
                                ...diffOptions,
                                tooltipText: fieldProps.tooltipText,
                                select: fieldProps.select ?? (resolvedType === 'select'),
                                multiLine: fieldProps.multiLine ?? fieldProps.multiline ?? (resolvedType === 'multiline'),
                            }}
                            stateProps={formikProps}
                            sx={fieldProps.sx ?? sx}
                        >
                            {children}
                        </SupplierProductLlmCustomField>
                    )}

                    <TooltipField tooltipText={fieldProps.tooltipText} />
                </Grid>
            </Grid>
        </>
    );
}

/**
 * Prototype local copy of CustomField.
 * This is where the diff display is added directly to TextField.
 */
interface SupplierProductLlmCustomFieldProps {
    stateProps?: {
        values?: any;
        handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
        touched?: any;
        errors?: any;
        handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
        state?: any;
        setState?: React.Dispatch<React.SetStateAction<any>>;
        submitCount?: number;
        status?: any;
    };
    options: {
        label?: string;
        disabled?: boolean;
        multiLine?: boolean;
        multiline?: boolean;
        select?: boolean;
        isMulti?: boolean;
        type?: string;
        name: string;
        height?: string;
        topMargin?: string;
        selectOpen?: boolean;
        className?: string;
        setSelectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
        rows?: number;
        maxRows?: number;
        placeholder?: string;
        warning?: string | ((ctx: { value: any; values: any }) => string | undefined);
        tooltipText?: string;
        llmDiffActive?: boolean;
        llmDiffOriginalValue?: any;
        llmDiffFieldName?: string;
        llmDiffOnRevert?: (fieldName: string) => Promise<void>;
    };
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

function SupplierProductLlmCustomField({ options, stateProps, children, sx }: SupplierProductLlmCustomFieldProps) {
    const { t } = useTranslation();

    const label = t(
        options.label ??
        options.name.charAt(0).toUpperCase() + options.name.slice(1)
    );

    const onChangeFunction =
        stateProps?.setState != undefined
            ? (event: React.ChangeEvent<HTMLInputElement>) => {
                stateProps?.setState(event.target.value);
            }
            : stateProps?.handleChange;

    let value = stateProps?.state ?? get(stateProps?.values ?? {}, options.name, '');

    if (
        options.select &&
        !options.isMulti &&
        (typeof value === 'object' || value === undefined)
    ) {
        value = '';
    }

    const [selectOpen, setSelectOpen] = useState(false);

    useEffect(() => {
        if (options.selectOpen !== undefined) {
            setSelectOpen(options.selectOpen);
        }
    }, [options.selectOpen]);

    const handleSelectOpen = () => {
        setSelectOpen(true);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(true);
        }
    };

    const handleSelectClose = () => {
        setSelectOpen(false);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(false);
        }
    };

    const isTouched = get(stateProps?.touched, options.name);
    const error = get(stateProps?.errors, options.name);
    const hasSubmitted = (stateProps?.submitCount ?? 0) > 0;
    const showError = (isTouched || hasSubmitted) && error;

    // --- generic warning logic ---
    const values = stateProps?.values ?? {};
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    let warningText = '';

    if (!showError && options.warning && (isTouched || hasSubmitted)) {
        if (typeof options.warning === 'string') {
            // only warn when empty
            if (isEmpty) {
                warningText = options.warning;
            }
        } else if (typeof options.warning === 'function') {
            warningText = options.warning({ value, values }) ?? '';
        }
    }

    // --- info from tooltip when FormInfoButton is active ---
    const status = stateProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText = options.tooltipText;

    const showTooltipInfo = showAllTooltips && !!tooltipText && !showError && !warningText;

    const helperText = showError ? error : warningText || (showTooltipInfo ? tooltipText : '');

    const isError = Boolean(showError);
    const isWarning = !isError && Boolean(warningText);
    const isInfo = !isError && !isWarning && showTooltipInfo;

    const diffActive = Boolean(options.llmDiffActive);
    const diffOriginalValue = formatSupplierProductLlmPreviewValue(options.llmDiffOriginalValue);

    const baseSx: SxProps<Theme> = {
        '&.MuiFormControl-root': {
            marginTop: options?.topMargin ?? '16px',
        },
        '& .MuiOutlinedInput-root': {
            height:
                options?.height == null ? 'auto' : options.height + '  !important',
        },
        '& label.MuiInputLabel-root': {
            marginTop: options?.height == null ? '0' : '-5px  !important',
        },
        ...(isWarning && {
            '& .MuiFormHelperText-root': {
                color: 'warning.main',
            },
        }),
        ...(isInfo && {
            '& .MuiFormHelperText-root': {
                color: 'info.main',
            },
        }),
    };

    const diffSx = diffActive
    ? {
        '& .MuiOutlinedInput-root': {
            position: 'relative',
            '& fieldset': {
                borderColor: '#86bde8',
            },
            '&:hover fieldset': {
                borderColor: '#86bde8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#86bde8',
            },
            '& .supplier-product-llm-revert-button': {
                opacity: 0,
                transition: 'opacity 120ms ease-in-out',
            },
            '&:hover .supplier-product-llm-revert-button': {
                opacity: 1,
            },
        },
        '& .MuiInputBase-input': {
            paddingTop: options.multiLine ? '22px !important' : '22px !important',
            paddingBottom: options.multiLine ? '8px !important' : '7px !important',
        },
        '& .MuiSelect-select': {
            paddingTop: '22px !important',
            paddingBottom: '7px !important',
        },
        '& textarea.MuiInputBase-input': {
            paddingTop: '22px !important',
        },
    }
    : {};

    return (
        <TextField
            label={label}
            name={options.name}
            value={value}
            className={
                options.className ??
                'custom-field-' + label?.toLowerCase().replace(' ', '-')
            }
            onBlur={stateProps?.handleBlur}
            multiline={options.multiLine ?? false}
            rows={options.rows}
            maxRows={options.maxRows}
            placeholder={options.placeholder}
            onChange={onChangeFunction}
            disabled={options.disabled ?? false}
            inputProps={{ readOnly: options.disabled }}
            select={options.select ?? false}
            SelectProps={{
                multiple: options.isMulti ?? false,
                open: selectOpen,
                onOpen: handleSelectOpen,
                onClose: handleSelectClose,
            }}
            InputLabelProps={diffActive ? { shrink: true } : undefined}
            InputProps={diffActive ? {
                startAdornment: (
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: 6,
                            left: 14,
                            right: options.select ? 70 : 46,
                            zIndex: 2,
                            fontStyle: 'italic',
                            fontSize: '0.68rem',
                            opacity: 0.8,
                            fontWeight: 500,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pointerEvents: 'none',
                        }}
                    >
                        {diffOriginalValue}
                    </Box>
                ),
                endAdornment: (
                    <IconButton
                        className="supplier-product-llm-revert-button"
                        size="small"
                        title="Revert to previous value"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (options.llmDiffFieldName && options.llmDiffOnRevert) {
                                options.llmDiffOnRevert(options.llmDiffFieldName);
                            }
                        }}
                        sx={{
                            mr: options.select ? 2.25 : 0,
                            color: '#86bde8',
                        }}
                    >
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                ),
            } : undefined}
            helperText={helperText}
            error={isError}
            type={options.type ?? 'text'}
            variant="outlined"
            fullWidth
            sx={[
                baseSx,
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                diffSx,
            ]}
            margin="normal"
        >
            {children}
        </TextField>
    );
}


/**
 * Supplier Product LLM API popup/config helpers.
 * Uses the same localStorage key/event as the Supplier page so both pages share one AI config.
 */
type SupplierProductLlmAiConfig = {
    url: string;
    apiKey: string;
    model: string;
};

const supplierLlmAiConfigStorageKey = "supplierLlmAiConfig";
const supplierLlmAiConfigChangedEvent = "supplierLlmAiConfigChanged";

const supplierProductLlmAiDefaultConfig: SupplierProductLlmAiConfig = {
    url: "https://api.openai.com/v1/chat/completions",
    apiKey: "",
    model: "gpt-5.4-mini-2026-03-17",
};

function SupplierProductLlmAiConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [config, setConfig] = useSupplierProductLlmAiConfig();
    const [draftConfig, setDraftConfig] = useState<SupplierProductLlmAiConfig>(config);

    useEffect(() => {
        if (open) {
            setDraftConfig(config);
        }
    }, [config, open]);

    const handleChange = (fieldName: keyof SupplierProductLlmAiConfig) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraftConfig((previousConfig) => ({ ...previousConfig, [fieldName]: event.target.value }));
    };

    const handleSave = () => {
        setConfig({
            url: draftConfig.url.trim() || supplierProductLlmAiDefaultConfig.url,
            apiKey: draftConfig.apiKey.trim(),
            model: draftConfig.model.trim() || supplierProductLlmAiDefaultConfig.model,
        });
        onClose();
    };

    const handleReset = () => {
        setDraftConfig(supplierProductLlmAiDefaultConfig);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>AI configuration</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="API URL"
                        value={draftConfig.url}
                        onChange={handleChange("url")}
                        fullWidth
                        helperText="Include protocol and port if needed, for example http://localhost:11434/v1/chat/completions."
                    />
                    <TextField
                        label="API key"
                        value={draftConfig.apiKey}
                        onChange={handleChange("apiKey")}
                        fullWidth
                        type="password"
                        autoComplete="off"
                        helperText="Stored in localStorage. Leave empty for local endpoints that do not require a key."
                    />
                    <TextField
                        label="Model"
                        value={draftConfig.model}
                        onChange={handleChange("model")}
                        fullWidth
                        helperText="Used in the OpenAI-compatible chat completions request body."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset}>Reset defaults</Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function SupplierProductLlmAiRunDialog({
    open,
    onClose,
    buildPrompt,
    applyLlmResponseText,
}: {
    open: boolean;
    onClose: () => void;
    buildPrompt: () => string;
    applyLlmResponseText: (responseText: string) => Promise<SupplierProductLlmApplyResult>;
}) {
    const [config] = useSupplierProductLlmAiConfig();
    const [userPrompt, setUserPrompt] = useState("");
    const [isRunning, setIsRunning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");

    const canRun = Boolean(userPrompt.trim()) && Boolean(config.url.trim()) && !isRunning;

    const handleRun = async () => {
        if (!canRun) {
            return;
        }

        setIsRunning(true);
        setErrorMessage("");
        setStatusMessage("");

        try {
            const responseText = await requestSupplierProductLlmAiResponse({
                config,
                userPrompt,
                formPrompt: buildPrompt(),
            });
            const applyResult = await applyLlmResponseText(responseText);

            if (applyResult === 'no-fields') {
                setErrorMessage('The AI response did not contain matching editable Supplier Product Formik fields. Expected either { "values": { ... } } or a JSON object with Supplier Product Formik field names.');
                return;
            }

            if (applyResult === 'no-changes') {
                setStatusMessage("AI response was valid, but it did not change any current field values.");
                return;
            }

            setStatusMessage("AI response applied to the form.");
        } catch (error) {
            console.error("Failed to run supplier product LLM request.", error);
            setErrorMessage(error instanceof Error ? error.message : "Could not run the AI request.");
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <Dialog open={open} onClose={isRunning ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>Run AI update</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="Prompt"
                        value={userPrompt}
                        onChange={(event) => setUserPrompt(event.target.value)}
                        fullWidth
                        multiline
                        rows={7}
                        disabled={isRunning}
                        placeholder="Example: Fill in missing supplier product fields from this text..."
                    />
                    {isRunning ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} />
                            <span>Calling AI and applying JSON response...</span>
                        </Box>
                    ) : null}
                    {statusMessage ? <Alert severity="success">{statusMessage}</Alert> : null}
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRunning}>Close</Button>
                <Button onClick={handleRun} variant="contained" disabled={!canRun}>Run</Button>
            </DialogActions>
        </Dialog>
    );
}

function useSupplierProductLlmAiConfig(): [SupplierProductLlmAiConfig, (nextConfig: SupplierProductLlmAiConfig) => void] {
    const [config, setConfigState] = useState<SupplierProductLlmAiConfig>(() => getStoredSupplierProductLlmAiConfig());

    const setConfig = useCallback((nextConfig: SupplierProductLlmAiConfig) => {
        setConfigState(nextConfig);
        saveStoredSupplierProductLlmAiConfig(nextConfig);
    }, []);

    useEffect(() => {
        const refreshConfig = () => setConfigState(getStoredSupplierProductLlmAiConfig());
        const handleStorage = (event: StorageEvent) => {
            if (event.key === supplierLlmAiConfigStorageKey) {
                refreshConfig();
            }
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener(supplierLlmAiConfigChangedEvent, refreshConfig);

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener(supplierLlmAiConfigChangedEvent, refreshConfig);
        };
    }, []);

    return [config, setConfig];
}

function getStoredSupplierProductLlmAiConfig(): SupplierProductLlmAiConfig {
    if (typeof window === "undefined") {
        return supplierProductLlmAiDefaultConfig;
    }

    try {
        const rawConfig = window.localStorage.getItem(supplierLlmAiConfigStorageKey);
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : {};

        return {
            ...supplierProductLlmAiDefaultConfig,
            ...parsedConfig,
            url: parsedConfig?.url || supplierProductLlmAiDefaultConfig.url,
            apiKey: parsedConfig?.apiKey || "",
            model: parsedConfig?.model || supplierProductLlmAiDefaultConfig.model,
        };
    } catch (error) {
        console.error("Failed to load supplier product LLM AI config from localStorage.", error);
        return supplierProductLlmAiDefaultConfig;
    }
}

function saveStoredSupplierProductLlmAiConfig(config: SupplierProductLlmAiConfig) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(supplierLlmAiConfigStorageKey, JSON.stringify(config));
    window.dispatchEvent(new Event(supplierLlmAiConfigChangedEvent));
}

async function requestSupplierProductLlmAiResponse({
    config,
    userPrompt,
    formPrompt,
}: {
    config: SupplierProductLlmAiConfig;
    userPrompt: string;
    formPrompt: string;
}) {
    const response = await fetch(config.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(config.apiKey ? { Authorization: `Bearer ${config.apiKey}` } : {}),
        },
        body: JSON.stringify({
            model: config.model || supplierProductLlmAiDefaultConfig.model,
            messages: [
                {
                    role: "system",
                    content: "You update Formik form values. Return only valid JSON. Prefer the { values: { ... } } shape. Do not return markdown unless it contains only one JSON codeblock.",
                },
                {
                    role: "user",
                    content: buildSupplierProductLlmAiPrompt(userPrompt, formPrompt),
                },
            ],
            temperature: 1,
        }),
    });

    const responseBodyText = await response.text();

    if (!response.ok) {
        throw new Error(getSupplierProductLlmApiErrorMessage(response.status, responseBodyText));
    }

    const responseBody = safeJsonParse(responseBodyText);
    const content = getSupplierProductLlmAiResponseContent(responseBody) ?? responseBodyText;
    const jsonText = extractJsonObjectText(content);

    if (!jsonText || jsonText === content.trim() && !content.trim().startsWith("{") && !content.trim().startsWith("```")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function buildSupplierProductLlmAiPrompt(userPrompt: string, formPrompt: string) {
    return [
        "User request:",
        userPrompt.trim(),
        "",
        "Form context and return instructions:",
        formPrompt,
    ].join("\n");
}

function getSupplierProductLlmAiResponseContent(responseBody: any) {
    const chatCompletionContent = responseBody?.choices?.[0]?.message?.content;

    if (typeof chatCompletionContent === "string") {
        return chatCompletionContent;
    }

    if (Array.isArray(chatCompletionContent)) {
        return chatCompletionContent.map((contentPart) => contentPart?.text ?? contentPart?.content ?? "").join("\n").trim();
    }

    const responseOutputText = responseBody?.output_text;

    if (typeof responseOutputText === "string") {
        return responseOutputText;
    }

    return null;
}

function getSupplierProductLlmApiErrorMessage(status: number, responseBodyText: string) {
    const parsedBody = safeJsonParse(responseBodyText);
    const apiMessage = parsedBody?.error?.message ?? parsedBody?.message;

    if (apiMessage) {
        return `AI request failed (${status}): ${apiMessage}`;
    }

    return `AI request failed (${status}).`;
}

function safeJsonParse(text: string) {
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

async function writeTextToClipboard(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    if (typeof document === "undefined") {
        throw new Error("Clipboard write is not available.");
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();

    const success = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (!success) {
        throw new Error("Fallback clipboard copy failed.");
    }
}

function parseJsonFromClipboardText(clipboardText: string) {
    const trimmedText = clipboardText.trim();
    const fencedJsonMatch = trimmedText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const jsonText = fencedJsonMatch?.[1] ?? extractJsonObjectText(trimmedText);

    return JSON.parse(jsonText);
}

function extractJsonObjectText(text: string) {
    const firstBraceIndex = text.indexOf("{");
    const lastBraceIndex = text.lastIndexOf("}");

    if (firstBraceIndex === -1 || lastBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) {
        return text;
    }

    return text.slice(firstBraceIndex, lastBraceIndex + 1);
}

function getFormikValuesFromLlmResponse(parsedResponse: any) {
    if (parsedResponse?.values && typeof parsedResponse.values === "object" && !Array.isArray(parsedResponse.values)) {
        return parsedResponse.values;
    }

    if (parsedResponse?.formValues && typeof parsedResponse.formValues === "object" && !Array.isArray(parsedResponse.formValues)) {
        return parsedResponse.formValues;
    }

    return parsedResponse;
}

function getAllowedSupplierProductFormikValues(nextValues: any, currentValues: any) {
    if (!nextValues || typeof nextValues !== "object" || Array.isArray(nextValues)) {
        return {};
    }

    return supplierProductLlmEditableFields.reduce((acc: any, fieldName) => {
        if (supplierProductLlmReadOnlyFields.includes(fieldName)) {
            return acc;
        }

        if (currentValues.ProductIsLocked && supplierProductLlmProductLockedFields.includes(fieldName)) {
            return acc;
        }

        if (Object.prototype.hasOwnProperty.call(nextValues, fieldName)) {
            acc[fieldName] = nextValues[fieldName];
        }

        return acc;
    }, {});
}

function getChangedSupplierProductFormikValues(allowedValues: any, currentValues: any) {
    return Object.keys(allowedValues).reduce((acc: any, fieldName) => {
        const currentValue = getValueByPath(currentValues, fieldName);
        const nextValue = allowedValues[fieldName];

        if (!areSupplierProductLlmValuesEqual(currentValue, nextValue)) {
            acc[fieldName] = nextValue;
        }

        return acc;
    }, {});
}

function getNextSupplierProductLlmChangedFields(previousChangedFields: SupplierProductLlmChangedFields, changedValues: any, currentValues: any) {
    const nextChangedFields: SupplierProductLlmChangedFields = { ...previousChangedFields };

    Object.keys(changedValues).forEach((fieldName) => {
        const originalValue = previousChangedFields[fieldName]?.originalValue ?? getValueByPath(currentValues, fieldName);
        const pastedValue = changedValues[fieldName];

        if (areSupplierProductLlmValuesEqual(originalValue, pastedValue)) {
            delete nextChangedFields[fieldName];
            return;
        }

        nextChangedFields[fieldName] = {
            fieldName,
            originalValue,
            pastedValue,
        };
    });

    return nextChangedFields;
}

function getSupplierProductLlmDiffOptions(fieldProps: any, formikProps: FormikProps<any>, supplierProductLlmClipboard: SupplierProductLlmClipboardController) {
    const fieldName = fieldProps?.name;
    const change = fieldName ? supplierProductLlmClipboard.changedFields[fieldName] : null;
    const currentValue = fieldName ? getValueByPath(formikProps.values, fieldName) : undefined;
    const isUnsupportedDiffField = Boolean(fieldProps.checkbox || fieldProps.date || fieldProps.disabled);
    const shouldShowDiff = Boolean(fieldName && change && !isUnsupportedDiffField && !areSupplierProductLlmValuesEqual(change.originalValue, currentValue));

    if (!shouldShowDiff) {
        return {};
    }

    return {
        llmDiffActive: true,
        llmDiffOriginalValue: change.originalValue,
        llmDiffFieldName: fieldName,
        llmDiffOnRevert: supplierProductLlmClipboard.revertField,
    };
}

function compactSupplierProductLabelValueOptions(options: any) {
    if (!Array.isArray(options)) {
        return {};
    }

    return options.reduce((acc: Record<string, any>, option: any) => {
        const label = option?.label ?? option?.Value ?? option?.Name ?? option?.text;
        const value = option?.value ?? option?.Key ?? option?.Id ?? option?.id;

        if (label === undefined || label === null || value === undefined || value === null) {
            return acc;
        }

        acc[String(label)] = value;
        return acc;
    }, {});
}

function compactSupplierProductProductOptions(products: any) {
    const rows = Array.isArray(products) ? products : [];
    const compactRows = rows
        .map((product: any) => {
            const productId = product?.ProductId ?? product?.Product ?? product?.Id ?? product?.id ?? product?.value ?? "";
            const name = product?.Name ?? product?.ProductName ?? product?.Value ?? product?.label ?? "";
            const manufacturer = product?.Manufacturer?.Key ?? product?.ManufacturerId ?? product?.Manufacturer ?? product?.ManufacturerValue ?? null;
            const manufacturerProductId = product?.ManufacturerProductId ?? product?.ManufacturerProduct ?? product?.ManufacturerProductCode ?? product?.ManufacturerItemId ?? "";

            return [productId, name, manufacturer, manufacturerProductId];
        })
        .filter((row: any[]) => row[0] !== "" || row[1] !== "");

    return {
        schema: ["ProductId", "Name", "Manufacturer", "ManufacturerProductId"],
        rowCount: compactRows.length,
        rows: compactRows,
    };
}
function getSupplierProductSelectorDialogProps(productSelector: any) {
    const dialogProps = { ...productSelector };
    delete dialogProps.productOptions;
    return dialogProps;
}

function getSupplierProductRowsFromSelector(productSelector: any) {
    const possibleProductRows = [
        productSelector?.productOptions,
        productSelector?.products,
        productSelector?.productRows,
        productSelector?.selectGrid?.[1]?.productOptions,
        productSelector?.selectGrid?.[1]?.products,
        productSelector?.selectGrid?.[1]?.productRows,
        productSelector?.selectGrid?.[1]?.rows,
        productSelector?.selectGrid?.[1]?.data,
        productSelector?.selectGrid?.[1]?.tableData,
        productSelector?.rows,
        productSelector?.data,
        productSelector?.tableData,
    ];

    const candidateRows = possibleProductRows.map((candidate) => Array.isArray(candidate) ? candidate : []);
    const productLikeRows = getLargestSupplierProductRows(candidateRows.map((rows) => rows.filter(isSupplierProductProductLikeRow)));

    if (productLikeRows.length > 0) {
        return productLikeRows;
    }

    return getLargestSupplierProductRows(candidateRows);
}

function getLargestSupplierProductRows(candidates: any[][]) {
    return candidates.reduce<any[]>((largest, rows) => rows.length > largest.length ? rows : largest, []);
}

function isSupplierProductProductLikeRow(row: any) {
    return Boolean(
        row &&
        typeof row === "object" &&
        (
            Object.prototype.hasOwnProperty.call(row, "ProductId") ||
            Object.prototype.hasOwnProperty.call(row, "Product") ||
            Object.prototype.hasOwnProperty.call(row, "ManufacturerProductId") ||
            Object.prototype.hasOwnProperty.call(row, "ManufacturerProduct") ||
            Object.prototype.hasOwnProperty.call(row, "ProductType")
        )
    );
}

function areSupplierProductLlmValuesEqual(firstValue: any, secondValue: any) {
    return normalizeSupplierProductLlmValue(firstValue) === normalizeSupplierProductLlmValue(secondValue);
}

function normalizeSupplierProductLlmValue(value: any) {
    if (value === undefined || value === null) {
        return "";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value).trim();
}

function formatSupplierProductLlmPreviewValue(value: any) {
    if (value === undefined || value === null || value === "") {
        return "Empty";
    }

    if (typeof value === "boolean") {
        return value ? "True" : "False";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

function getValueByPath(values: any, path: string) {
    return path.split(".").reduce((acc, pathPart) => acc?.[pathPart], values);
}

function getPageNameText(pageName: string | { text: string; after?: React.ReactNode }) {
    return typeof pageName === "string" ? pageName : pageName.text;
}

const toBoolean = (value: any) => {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return Boolean(value);
};

export default EditSupplierProductWithAiButtons;